import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { 
  Globe, Building2, FileText, Send, CheckCircle2, Clock, AlertTriangle,
  Plus, Search, RefreshCw, ExternalLink, Settings, Upload, Download,
  ChevronRight, Eye, Edit, Trash2, Plug, Shield, Key, AlertCircle, Save, Loader2,
  BarChart3, TrendingUp, Calendar, Target, Activity, PieChart
} from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import PipelineDashboardTab from '@/components/regulatory/PipelineDashboardTab';

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL;

export default function RegulatoryAPIPage() {
  const { t } = useTranslation();
  const token = localStorage.getItem('token');
  const [authorities, setAuthorities] = useState([]);
  const [apiConfigs, setApiConfigs] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [selectedAuthority, setSelectedAuthority] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [complianceData, setComplianceData] = useState(null);
  const [regulatoryDeadlines, setRegulatoryDeadlines] = useState([]);
  const [showDeadlineDialog, setShowDeadlineDialog] = useState(false);
  const [deadlineForm, setDeadlineForm] = useState({
    authority: 'gaca',
    requirement: '',
    due_date: '',
    notes: ''
  });
  
  // API Config form
  const [configForm, setConfigForm] = useState({
    api_base_url: '',
    api_version: '',
    auth_type: 'oauth2',
    client_id: '',
    client_secret: '',
    certificate_path: '',
    operator_code: '',
    aoc_number: '',
    organization_name: '',
    notes: '',
    status: 'configured'
  });
  
  // Filters
  const [authorityFilter, setAuthorityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Enums
  const [reportTypes, setReportTypes] = useState([]);
  const [occurrenceCategories, setOccurrenceCategories] = useState([]);
  const [flightPhases, setFlightPhases] = useState([]);

  const apiHeaders = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  const fetchDeadlines = async () => {
    try {
      const res = await fetch(`${API_URL}/api/regulatory/deadlines`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setRegulatoryDeadlines(data.deadlines || []);
      }
    } catch (err) {
      console.error('Error fetching deadlines:', err);
    }
  };

  const createDeadline = async () => {
    try {
      const params = new URLSearchParams({
        authority: deadlineForm.authority,
        requirement: deadlineForm.requirement,
        due_date: deadlineForm.due_date
      });
      if (deadlineForm.notes) params.append('notes', deadlineForm.notes);
      
      const res = await fetch(`${API_URL}/api/regulatory/deadlines?${params}`, {
        method: 'POST',
        headers: apiHeaders
      });
      
      if (res.ok) {
        toast.success('Deadline created successfully');
        setShowDeadlineDialog(false);
        setDeadlineForm({ authority: 'gaca', requirement: '', due_date: '', notes: '' });
        fetchDeadlines();
      } else {
        toast.error('Failed to create deadline');
      }
    } catch (err) {
      toast.error('Error creating deadline');
    }
  };

  const markDeadlineComplete = async (deadlineId) => {
    try {
      const res = await fetch(`${API_URL}/api/regulatory/deadlines/${deadlineId}/complete`, {
        method: 'POST',
        headers: apiHeaders
      });
      
      if (res.ok) {
        toast.success('Deadline marked as complete');
        fetchDeadlines();
      }
    } catch (err) {
      toast.error('Error completing deadline');
    }
  };

  const fetchAuthorities = async () => {
    try {
      const res = await fetch(`${API_URL}/api/regulatory/authorities`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setAuthorities(data.authorities || []);
      }
    } catch (err) {
      console.error('Error fetching authorities:', err);
    }
  };

  const fetchApiConfigs = async () => {
    try {
      const res = await fetch(`${API_URL}/api/regulatory/configs`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setApiConfigs(data.configs || []);
      }
    } catch (err) {
      console.error('Error fetching configs:', err);
    }
  };

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/regulatory/submissions?limit=50`;
      if (authorityFilter) url += `&authority=${authorityFilter}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (searchQuery) url += `&search=${searchQuery}`;
      
      const res = await fetch(url, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setSubmissions(data.submissions || []);
      }
    } catch (err) {
      console.error('Error fetching submissions:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/regulatory/submissions/stats`, { headers: apiHeaders });
      if (res.ok) setStats(await res.json());
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const fetchEnums = async () => {
    try {
      const [typesRes, catsRes, phasesRes] = await Promise.all([
        fetch(`${API_URL}/api/regulatory/enums/report-types`, { headers: apiHeaders }),
        fetch(`${API_URL}/api/regulatory/reference/occurrence-categories`, { headers: apiHeaders }),
        fetch(`${API_URL}/api/regulatory/reference/flight-phases`, { headers: apiHeaders })
      ]);
      
      if (typesRes.ok) setReportTypes(await typesRes.json());
      if (catsRes.ok) setOccurrenceCategories(await catsRes.json());
      if (phasesRes.ok) setFlightPhases(await phasesRes.json());
    } catch (err) {
      console.error('Error fetching enums:', err);
    }
  };

  const fetchSubmissionDetail = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/regulatory/submissions/${id}`, { headers: apiHeaders });
      if (res.ok) {
        setSelectedSubmission(await res.json());
        setShowDetailDialog(true);
      }
    } catch (err) {
      toast.error('Error fetching submission details');
    }
  };

  const openConfigDialog = (authority) => {
    setSelectedAuthority(authority);
    // Load existing config if available
    const existingConfig = apiConfigs.find(c => c.authority === authority.id);
    if (existingConfig) {
      setConfigForm({
        api_base_url: existingConfig.api_base_url || '',
        api_version: existingConfig.api_version || '',
        auth_type: existingConfig.auth_type || 'oauth2',
        client_id: existingConfig.client_id || '',
        client_secret: '', // Don't load secret for security
        certificate_path: existingConfig.certificate_path || '',
        operator_code: existingConfig.operator_code || '',
        aoc_number: existingConfig.aoc_number || '',
        organization_name: existingConfig.organization_name || '',
        notes: existingConfig.notes || '',
        status: existingConfig.status || 'configured'
      });
    } else {
      setConfigForm({
        api_base_url: '',
        api_version: '',
        auth_type: 'oauth2',
        client_id: '',
        client_secret: '',
        certificate_path: '',
        operator_code: '',
        aoc_number: '',
        organization_name: '',
        notes: '',
        status: 'configured'
      });
    }
    setShowConfigDialog(true);
  };

  const saveApiConfig = async () => {
    if (!selectedAuthority) return;
    setSavingConfig(true);
    try {
      const configData = {
        authority: selectedAuthority.id,
        ...configForm,
        endpoints: {
          occurrence_submit: `${configForm.api_base_url}/api/v1/occurrences`,
          status_check: `${configForm.api_base_url}/api/v1/status/{id}`
        }
      };
      
      const res = await fetch(`${API_URL}/api/regulatory/configs`, {
        method: 'POST',
        headers: apiHeaders,
        body: JSON.stringify(configData)
      });
      
      if (res.ok) {
        toast.success('API configuration saved');
        setShowConfigDialog(false);
        fetchAuthorities();
        fetchApiConfigs();
      } else {
        toast.error('Failed to save configuration');
      }
    } catch (err) {
      toast.error('Error saving configuration');
    } finally {
      setSavingConfig(false);
    }
  };

  const testConnection = async (authorityId) => {
    try {
      const res = await fetch(`${API_URL}/api/regulatory/configs/${authorityId}/test`, {
        method: 'POST',
        headers: apiHeaders
      });
      if (res.ok) {
        const data = await res.json();
        toast.info(data.message);
      }
    } catch (err) {
      toast.error('Error testing connection');
    }
  };

  // Calculate compliance dashboard data
  const calculateComplianceData = () => {
    const configuredAuthorities = authorities.filter(a => 
      a.api_integration_status === 'configured' || apiConfigs.find(c => c.authority === a.id)
    );
    
    const pendingSubmissions = submissions.filter(s => 
      ['draft', 'pending_review', 'ready_to_submit'].includes(s.status)
    );
    
    const overdueSubmissions = submissions.filter(s => {
      if (!s.due_date) return false;
      return new Date(s.due_date) < new Date() && !['submitted', 'acknowledged', 'closed'].includes(s.status);
    });
    
    // Use real deadlines from API (formatted for dashboard display)
    const formattedDeadlines = regulatoryDeadlines
      .filter(d => d.status !== 'completed')
      .map(d => ({
        id: d.id,
        authority: d.authority,
        requirement: d.requirement,
        due: d.due_date,
        status: d.status,
        daysLeft: d.days_left,
        isDefault: d.is_default
      }))
      .sort((a, b) => a.daysLeft - b.daysLeft);
    
    // Calculate compliance score per authority
    const authorityCompliance = authorities.map(auth => {
      const isConfigured = auth.api_integration_status === 'configured' || apiConfigs.find(c => c.authority === auth.id);
      const authSubmissions = submissions.filter(s => s.authority === auth.id);
      const submitted = authSubmissions.filter(s => ['submitted', 'acknowledged', 'closed'].includes(s.status)).length;
      const total = authSubmissions.length || 1;
      const submissionRate = Math.round((submitted / total) * 100);
      
      // Calculate overall compliance score
      const configScore = isConfigured ? 30 : 0;
      const submissionScore = submissionRate * 0.5;
      const activityScore = authSubmissions.length > 0 ? 20 : 0;
      
      return {
        ...auth,
        isConfigured,
        totalSubmissions: authSubmissions.length,
        submittedCount: submitted,
        pendingCount: authSubmissions.filter(s => ['draft', 'pending_review'].includes(s.status)).length,
        complianceScore: Math.min(100, Math.round(configScore + submissionScore + activityScore)),
        lastSubmission: authSubmissions.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]?.created_at
      };
    });
    
    // Overall compliance metrics
    const overallScore = authorities.length > 0 
      ? Math.round(authorityCompliance.reduce((sum, a) => sum + a.complianceScore, 0) / authorities.length)
      : 0;
    
    return {
      configuredCount: configuredAuthorities.length,
      totalAuthorities: authorities.length,
      configurationRate: authorities.length > 0 ? Math.round((configuredAuthorities.length / authorities.length) * 100) : 0,
      pendingSubmissions: pendingSubmissions.length,
      overdueSubmissions: overdueSubmissions.length,
      totalSubmissions: submissions.length,
      acknowledgedSubmissions: submissions.filter(s => s.status === 'acknowledged').length,
      regulatoryDeadlines: formattedDeadlines,
      authorityCompliance,
      overallScore,
      submissionsByAuthority: stats?.by_authority || {},
      submissionsByType: stats?.by_type || {}
    };
  };

  useEffect(() => {
    fetchAuthorities();
    fetchApiConfigs();
    fetchSubmissions();
    fetchStats();
    fetchEnums();
    fetchDeadlines();
  }, []);

  useEffect(() => {
    fetchSubmissions();
  }, [authorityFilter, statusFilter, searchQuery]);

  useEffect(() => {
    if (authorities.length > 0) {
      setComplianceData(calculateComplianceData());
    }
  }, [authorities, apiConfigs, submissions, stats, regulatoryDeadlines]);

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
      pending_review: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
      ready_to_submit: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      submitted: 'bg-green-500/20 text-green-300 border-green-500/30',
      acknowledged: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
      under_investigation: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
      closed: 'bg-gray-500/20 text-gray-300 border-gray-500/30',
      rejected: 'bg-red-500/20 text-red-300 border-red-500/30'
    };
    return styles[status] || styles.draft;
  };

  const getAuthorityLogo = (authority) => {
    const logos = {
      gaca: '🇸🇦',
      easa: '🇪🇺',
      faa: '🇺🇸',
      icao: '🌍',
      caa_uk: '🇬🇧',
      casa: '🇦🇺',
      tcca: '🇨🇦'
    };
    return logos[authority] || '🌐';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6" data-testid="regulatory-api-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Globe className="w-8 h-8 text-blue-400" />
          <div>
            <h1 className="text-2xl font-bold text-slate-100">{t('regulatory.title')}</h1>
            <p className="text-sm text-slate-400">{t('regulatory.subtitle')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => { fetchSubmissions(); fetchStats(); }}>
            <RefreshCw className="w-4 h-4 mr-2" />
            {t('common.refresh')}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">{t('common.total')} {t('regulatory.submissions')}</p>
                <p className="text-2xl font-bold text-slate-100">{stats?.total || 0}</p>
              </div>
              <FileText className="w-8 h-8 text-slate-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">{t('policies.statuses.draft')}</p>
                <p className="text-2xl font-bold text-slate-400">{stats?.draft || 0}</p>
              </div>
              <Edit className="w-8 h-8 text-slate-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">{t('common.submit')}</p>
                <p className="text-2xl font-bold text-green-400">{stats?.submitted || 0}</p>
              </div>
              <Send className="w-8 h-8 text-green-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Acknowledged</p>
                <p className="text-2xl font-bold text-emerald-400">{stats?.acknowledged || 0}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-emerald-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Authorities</p>
                <p className="text-2xl font-bold text-blue-400">{authorities?.length || 0}</p>
              </div>
              <Building2 className="w-8 h-8 text-blue-500/50" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800/50 mb-6">
          <TabsTrigger value="dashboard" data-testid="compliance-dashboard-tab">
            <BarChart3 className="w-4 h-4 mr-2" />
            Compliance Dashboard
          </TabsTrigger>
          <TabsTrigger value="submissions">Submissions</TabsTrigger>
          <TabsTrigger value="authorities">Regulatory Authorities</TabsTrigger>
          <TabsTrigger value="direct-api" data-testid="direct-api-tab">
            <Plug className="w-4 h-4 mr-2" />
            Direct API (Phase 3)
          </TabsTrigger>
          <TabsTrigger value="pipeline" data-testid="pipeline-tab">
            <Activity className="w-4 h-4 mr-2" />
            Submission Pipeline
          </TabsTrigger>
          <TabsTrigger value="reference">Reference Data</TabsTrigger>
        </TabsList>

        {/* COMPLIANCE DASHBOARD TAB */}
        <TabsContent value="dashboard" data-testid="compliance-dashboard-content">
          {complianceData ? (
            <div className="space-y-6">
              {/* Overall Compliance Score */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card className="bg-gradient-to-br from-blue-900/40 to-slate-800/50 border-blue-700/50 lg:col-span-1">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Target className="w-5 h-5 text-blue-400" />
                      Overall Compliance Score
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-center py-4">
                      <div className="relative w-32 h-32">
                        <svg className="w-full h-full transform -rotate-90">
                          <circle cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="12" fill="none" className="text-slate-700" />
                          <circle 
                            cx="64" cy="64" r="56" 
                            stroke="currentColor" 
                            strokeWidth="12" 
                            fill="none" 
                            strokeDasharray={`${(complianceData.overallScore / 100) * 352} 352`}
                            className={complianceData.overallScore >= 80 ? 'text-emerald-400' : complianceData.overallScore >= 50 ? 'text-yellow-400' : 'text-red-400'}
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className={`text-3xl font-bold ${complianceData.overallScore >= 80 ? 'text-emerald-400' : complianceData.overallScore >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>
                            {complianceData.overallScore}%
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="text-center text-sm text-slate-400">
                      {complianceData.overallScore >= 80 ? 'Excellent Compliance' : 
                       complianceData.overallScore >= 50 ? 'Needs Improvement' : 'Critical - Action Required'}
                    </div>
                  </CardContent>
                </Card>

                {/* Configuration Status */}
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Settings className="w-5 h-5 text-purple-400" />
                      API Configuration Status
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400">Configured</span>
                      <span className="text-2xl font-bold text-purple-400">
                        {complianceData.configuredCount}/{complianceData.totalAuthorities}
                      </span>
                    </div>
                    <Progress value={complianceData.configurationRate} className="h-2" />
                    <div className="grid grid-cols-2 gap-4 pt-2">
                      <div className="text-center p-3 bg-slate-900/50 rounded-lg">
                        <p className="text-2xl font-bold text-green-400">{complianceData.configuredCount}</p>
                        <p className="text-xs text-slate-500">Connected</p>
                      </div>
                      <div className="text-center p-3 bg-slate-900/50 rounded-lg">
                        <p className="text-2xl font-bold text-slate-400">{complianceData.totalAuthorities - complianceData.configuredCount}</p>
                        <p className="text-xs text-slate-500">Pending</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Submission Summary */}
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Activity className="w-5 h-5 text-cyan-400" />
                      Submission Activity
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between p-2 bg-slate-900/50 rounded">
                      <span className="text-slate-400 flex items-center gap-2">
                        <FileText className="w-4 h-4" /> Total Submissions
                      </span>
                      <span className="font-bold text-white">{complianceData.totalSubmissions}</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-yellow-500/10 rounded border border-yellow-500/20">
                      <span className="text-yellow-400 flex items-center gap-2">
                        <Clock className="w-4 h-4" /> Pending
                      </span>
                      <span className="font-bold text-yellow-400">{complianceData.pendingSubmissions}</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-emerald-500/10 rounded border border-emerald-500/20">
                      <span className="text-emerald-400 flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4" /> Acknowledged
                      </span>
                      <span className="font-bold text-emerald-400">{complianceData.acknowledgedSubmissions}</span>
                    </div>
                    {complianceData.overdueSubmissions > 0 && (
                      <div className="flex items-center justify-between p-2 bg-red-500/10 rounded border border-red-500/20">
                        <span className="text-red-400 flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" /> Overdue
                        </span>
                        <span className="font-bold text-red-400">{complianceData.overdueSubmissions}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Authority Compliance Cards */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-blue-400" />
                    Compliance by Authority
                  </CardTitle>
                  <CardDescription>Track compliance status across all regulatory authorities</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {complianceData.authorityCompliance.map(auth => (
                      <div 
                        key={auth.id} 
                        className={`p-4 rounded-lg border ${auth.isConfigured ? 'bg-slate-900/50 border-slate-600' : 'bg-slate-900/30 border-slate-700/50'}`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-2xl">{getAuthorityLogo(auth.id)}</span>
                            <div>
                              <p className="font-medium text-white">{auth.name}</p>
                              <p className="text-xs text-slate-500">{auth.occurrence_system || auth.id.toUpperCase()}</p>
                            </div>
                          </div>
                          <Badge className={auth.isConfigured ? 'bg-green-500/20 text-green-300 border-green-500/30' : 'bg-slate-500/20 text-slate-400 border-slate-500/30'}>
                            {auth.isConfigured ? 'Active' : 'Pending'}
                          </Badge>
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-400">Compliance</span>
                            <span className={`font-medium ${auth.complianceScore >= 80 ? 'text-emerald-400' : auth.complianceScore >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>
                              {auth.complianceScore}%
                            </span>
                          </div>
                          <Progress 
                            value={auth.complianceScore} 
                            className="h-1.5"
                          />
                          <div className="flex items-center justify-between text-xs text-slate-500 pt-1">
                            <span>{auth.totalSubmissions} submissions</span>
                            <span>{auth.pendingCount} pending</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Upcoming Deadlines */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-orange-400" />
                      Upcoming Regulatory Deadlines
                    </CardTitle>
                    <CardDescription>Track compliance deadlines across GACA, EASA, FAA, and ICAO requirements</CardDescription>
                  </div>
                  <Button 
                    size="sm" 
                    onClick={() => setShowDeadlineDialog(true)}
                    className="bg-orange-500 hover:bg-orange-600"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Deadline
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {complianceData.regulatoryDeadlines.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No upcoming deadlines</p>
                        <p className="text-sm">Add regulatory deadlines to track compliance</p>
                      </div>
                    ) : (
                      complianceData.regulatoryDeadlines.map((deadline, idx) => (
                        <div 
                          key={deadline.id || idx}
                          className={`flex items-center justify-between p-4 rounded-lg border ${
                            deadline.daysLeft <= 14 ? 'bg-red-500/10 border-red-500/30' :
                            deadline.daysLeft <= 30 ? 'bg-yellow-500/10 border-yellow-500/30' :
                            'bg-slate-900/50 border-slate-700'
                          }`}
                        >
                          <div className="flex items-center gap-4">
                            <span className="text-2xl">{getAuthorityLogo(deadline.authority)}</span>
                            <div>
                              <p className="font-medium text-white">{deadline.requirement}</p>
                              <p className="text-sm text-slate-400">
                                {deadline.authority.toUpperCase()} Requirement
                                {deadline.isDefault && <span className="ml-2 text-xs text-slate-500">(Standard)</span>}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className={`font-medium ${
                                deadline.daysLeft <= 14 ? 'text-red-400' :
                                deadline.daysLeft <= 30 ? 'text-yellow-400' :
                                'text-slate-300'
                              }`}>
                                {deadline.daysLeft} days left
                              </p>
                              <p className="text-sm text-slate-500">Due: {deadline.due}</p>
                            </div>
                            {!deadline.isDefault && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-green-500/30 text-green-400 hover:bg-green-500/20"
                                onClick={() => markDeadlineComplete(deadline.id)}
                              >
                                <CheckCircle2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <p className="text-sm text-blue-300 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Email notifications are sent automatically at 30, 14, and 7 days before each deadline
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-400" />
                    Quick Actions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Button 
                      variant="outline" 
                      className="h-auto py-4 flex flex-col items-center gap-2 bg-slate-900/50 border-slate-600 hover:bg-slate-800"
                      onClick={() => setActiveTab('submissions')}
                    >
                      <Plus className="w-5 h-5 text-blue-400" />
                      <span className="text-sm">New Submission</span>
                    </Button>
                    <Button 
                      variant="outline" 
                      className="h-auto py-4 flex flex-col items-center gap-2 bg-slate-900/50 border-slate-600 hover:bg-slate-800"
                      onClick={() => setActiveTab('authorities')}
                    >
                      <Settings className="w-5 h-5 text-purple-400" />
                      <span className="text-sm">Configure API</span>
                    </Button>
                    <Button 
                      variant="outline" 
                      className="h-auto py-4 flex flex-col items-center gap-2 bg-slate-900/50 border-slate-600 hover:bg-slate-800"
                      onClick={() => {
                        fetchAuthorities();
                        fetchApiConfigs();
                        fetchSubmissions();
                        fetchStats();
                        toast.success('Dashboard refreshed');
                      }}
                    >
                      <RefreshCw className="w-5 h-5 text-green-400" />
                      <span className="text-sm">Refresh Data</span>
                    </Button>
                    <Button 
                      variant="outline" 
                      className="h-auto py-4 flex flex-col items-center gap-2 bg-slate-900/50 border-slate-600 hover:bg-slate-800"
                      onClick={() => setActiveTab('reference')}
                    >
                      <FileText className="w-5 h-5 text-cyan-400" />
                      <span className="text-sm">Reference Data</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
                <span className="ml-3 text-slate-400">Loading compliance data...</span>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="submissions">
          {/* Filters */}
          <Card className="bg-slate-800/50 border-slate-700 mb-6">
            <CardContent className="p-4">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    placeholder="Search submissions..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-slate-900/50"
                  />
                </div>
                <Select value={authorityFilter || 'all'} onValueChange={(v) => setAuthorityFilter(v === 'all' ? '' : v)}>
                  <SelectTrigger className="w-[180px] bg-slate-900/50">
                    <SelectValue placeholder="Authority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Authorities</SelectItem>
                    {authorities?.map(a => (
                      <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
                  <SelectTrigger className="w-[150px] bg-slate-900/50">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="submitted">Submitted</SelectItem>
                    <SelectItem value="acknowledged">Acknowledged</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={() => { setSearchQuery(''); setAuthorityFilter(''); setStatusFilter(''); }}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Clear
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Submissions List */}
          <div className="space-y-4">
            {loading ? (
              <p className="text-center text-slate-500 py-8">Loading...</p>
            ) : submissions.length === 0 ? (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-8 text-center">
                  <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400 mb-4">No regulatory submissions yet.</p>
                  <p className="text-sm text-slate-500">Submissions can be created from incident reports or manually.</p>
                </CardContent>
              </Card>
            ) : (
              submissions.map(sub => (
                <Card 
                  key={sub.id} 
                  className="bg-slate-800/50 border-slate-700 hover:border-blue-500/50 transition-all cursor-pointer"
                  onClick={() => fetchSubmissionDetail(sub.id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <span className="text-2xl">{getAuthorityLogo(sub.authority)}</span>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono text-sm text-blue-400">{sub.submission_reference}</span>
                            <Badge className={getStatusBadge(sub.status)}>{sub.status?.replace(/_/g, ' ')}</Badge>
                            <Badge variant="outline">{sub.report_type?.replace(/_/g, ' ')}</Badge>
                          </div>
                          <p className="text-sm text-slate-300">{sub.report_data?.headline || 'No headline'}</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-500" />
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="authorities">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {authorities.map(auth => {
              const config = apiConfigs.find(c => c.authority === auth.id);
              const hasConfig = !!config;
              
              return (
                <Card key={auth.id} className="bg-slate-800/50 border-slate-700">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <span className="text-3xl">{getAuthorityLogo(auth.id)}</span>
                        <div>
                          <h3 className="font-semibold text-slate-100">{auth.name}</h3>
                          <p className="text-sm text-slate-400">{auth.occurrence_system}</p>
                        </div>
                      </div>
                      <Badge className={
                        auth.api_integration_status === 'active' ? 'bg-green-500/20 text-green-300' : 
                        hasConfig ? 'bg-blue-500/20 text-blue-300' : 
                        'bg-yellow-500/20 text-yellow-300'
                      }>
                        {auth.api_integration_status === 'active' ? 'Connected' : hasConfig ? 'Configured' : 'Not Configured'}
                      </Badge>
                    </div>
                    
                    {/* Config Summary */}
                    {hasConfig && (
                      <div className="mb-4 p-3 rounded bg-slate-900/50 space-y-2 text-xs">
                        <div className="flex justify-between">
                          <span className="text-slate-500">API URL:</span>
                          <span className="text-slate-300 font-mono">{config.api_base_url || 'Not set'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Auth Type:</span>
                          <span className="text-slate-300">{config.auth_type?.toUpperCase() || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Operator Code:</span>
                          <span className="text-slate-300">{config.operator_code || 'Not set'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Last Updated:</span>
                          <span className="text-slate-300">{config.configured_at ? new Date(config.configured_at).toLocaleDateString() : 'N/A'}</span>
                        </div>
                      </div>
                    )}
                    
                    <Separator className="bg-slate-700 my-4" />
                    
                    <div className="space-y-3">
                      {auth.portal && (
                        <a 
                          href={auth.portal} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Visit Portal
                        </a>
                      )}
                      
                      {!hasConfig && (
                        <div className="p-3 rounded bg-yellow-500/10 border border-yellow-500/30">
                          <div className="flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 text-yellow-400 mt-0.5" />
                            <div className="text-xs text-yellow-300">
                              <p className="font-medium mb-1">API Not Configured</p>
                              <p>Configure API credentials to enable direct submission to {auth.name}.</p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => openConfigDialog(auth)} data-testid={`configure-${auth.id}-btn`}>
                          <Settings className="w-4 h-4 mr-1" />
                          {hasConfig ? 'Edit Config' : 'Configure API'}
                        </Button>
                        {hasConfig && (
                          <Button variant="outline" size="sm" onClick={() => testConnection(auth.id)}>
                            <Plug className="w-4 h-4 mr-1" />
                            Test
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* DIRECT API (PHASE 3) TAB */}
        <TabsContent value="direct-api" data-testid="direct-api-content">
          <div className="space-y-6">
            {/* Phase 3 Status Banner */}
            <Card className="bg-gradient-to-r from-purple-900/40 to-blue-900/40 border-purple-700/50">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-xl bg-purple-500/20">
                      <Plug className="w-8 h-8 text-purple-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">Direct API Integration - Phase 3</h3>
                      <p className="text-slate-400 mt-1">Configure direct API connections to regulatory authorities for automated submissions</p>
                    </div>
                  </div>
                  <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 px-3 py-1">
                    <Clock className="w-3 h-3 mr-1" />
                    Pending Formal Agreements
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Integration Roadmap */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-slate-100 flex items-center gap-2">
                  <Target className="w-5 h-5 text-blue-400" />
                  Integration Roadmap
                </CardTitle>
                <CardDescription>Progress toward direct API integration with regulatory authorities</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Phase Progress */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-300">Overall Progress</span>
                      <span className="text-sm text-slate-400">Phase 3 of 3</span>
                    </div>
                    <Progress value={75} className="h-2" />
                  </div>

                  {/* Phase Steps */}
                  <div className="space-y-4">
                    {[
                      { phase: 1, name: 'ECCAIRS XML Export', status: 'completed', description: 'Export reports in ECCAIRS-compatible XML format', icon: CheckCircle2, color: 'green' },
                      { phase: 2, name: 'Email Submission', status: 'completed', description: 'Automated email submission with XML attachments', icon: CheckCircle2, color: 'green' },
                      { phase: 3, name: 'Direct API Integration', status: 'in_progress', description: 'Real-time API connections to authority systems', icon: Clock, color: 'yellow' }
                    ].map((step) => (
                      <div key={step.phase} className={`flex items-start gap-4 p-4 rounded-lg ${step.status === 'completed' ? 'bg-green-500/10 border border-green-500/30' : 'bg-yellow-500/10 border border-yellow-500/30'}`}>
                        <div className={`p-2 rounded-lg ${step.status === 'completed' ? 'bg-green-500/20' : 'bg-yellow-500/20'}`}>
                          <step.icon className={`w-5 h-5 ${step.status === 'completed' ? 'text-green-400' : 'text-yellow-400'}`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium text-slate-200">Phase {step.phase}: {step.name}</h4>
                            <Badge className={step.status === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                              {step.status === 'completed' ? 'Complete' : 'In Progress'}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-400 mt-1">{step.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Authority API Configurations */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-slate-100 flex items-center gap-2">
                      <Settings className="w-5 h-5 text-slate-400" />
                      API Configurations
                    </CardTitle>
                    <CardDescription>Configure API credentials for each regulatory authority</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {authorities.map((auth) => {
                    const config = apiConfigs.find(c => c.authority === auth.id);
                    const isConfigured = config && config.api_base_url;
                    
                    return (
                      <div key={auth.id} className="flex items-center justify-between p-4 rounded-lg bg-slate-900/50 border border-slate-700">
                        <div className="flex items-center gap-4">
                          <div className={`p-2 rounded-lg ${isConfigured ? 'bg-blue-500/20' : 'bg-slate-600/30'}`}>
                            <Globe className={`w-5 h-5 ${isConfigured ? 'text-blue-400' : 'text-slate-500'}`} />
                          </div>
                          <div>
                            <h4 className="font-medium text-slate-200">{auth.name}</h4>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-xs">
                                {auth.occurrence_system || 'Web Portal'}
                              </Badge>
                              {isConfigured ? (
                                <Badge className="bg-blue-500/20 text-blue-400 text-xs">
                                  <Key className="w-3 h-3 mr-1" />
                                  Configured
                                </Badge>
                              ) : (
                                <Badge className="bg-slate-500/20 text-slate-400 text-xs">
                                  Not Configured
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              setSelectedAuthority(auth);
                              if (config) {
                                setConfigForm({
                                  api_base_url: config.api_base_url || '',
                                  api_version: config.api_version || '',
                                  auth_type: config.auth_type || 'oauth2',
                                  client_id: config.client_id || '',
                                  client_secret: '',
                                  certificate_path: config.certificate_path || '',
                                  operator_code: config.operator_code || '',
                                  aoc_number: config.aoc_number || '',
                                  organization_name: config.organization_name || '',
                                  notes: config.notes || '',
                                  status: config.status || 'configured'
                                });
                              } else {
                                setConfigForm({
                                  api_base_url: '',
                                  api_version: '',
                                  auth_type: 'oauth2',
                                  client_id: '',
                                  client_secret: '',
                                  certificate_path: '',
                                  operator_code: '',
                                  aoc_number: '',
                                  organization_name: '',
                                  notes: '',
                                  status: 'configured'
                                });
                              }
                              setShowConfigDialog(true);
                            }}
                          >
                            <Settings className="w-4 h-4 mr-1" />
                            Configure
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            disabled={!isConfigured}
                            onClick={async () => {
                              try {
                                const res = await fetch(`${API_URL}/api/regulatory/configs/${auth.id}/test`, {
                                  method: 'POST',
                                  headers: apiHeaders
                                });
                                const data = await res.json();
                                if (data.test_result === 'pending_formal_agreement') {
                                  toast.info(data.message, { duration: 8000 });
                                } else {
                                  toast.success('Connection test successful');
                                }
                              } catch (err) {
                                toast.error('Connection test failed');
                              }
                            }}
                          >
                            <RefreshCw className="w-4 h-4 mr-1" />
                            Test
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Requirements & Next Steps */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-slate-100 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-amber-400" />
                    Requirements for Direct API
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {[
                      { text: 'Formal agreement with regulatory authority', status: 'pending' },
                      { text: 'API access credentials (OAuth2/Certificate)', status: 'pending' },
                      { text: 'Organization verification & AOC number', status: 'ready' },
                      { text: 'ECCAIRS-compatible data mapping', status: 'complete' },
                      { text: 'Secure credential storage', status: 'complete' },
                      { text: 'Submission audit trail', status: 'complete' }
                    ].map((req, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-2 rounded bg-slate-900/50">
                        {req.status === 'complete' ? (
                          <CheckCircle2 className="w-4 h-4 text-green-400" />
                        ) : req.status === 'ready' ? (
                          <CheckCircle2 className="w-4 h-4 text-blue-400" />
                        ) : (
                          <Clock className="w-4 h-4 text-amber-400" />
                        )}
                        <span className="text-sm text-slate-300">{req.text}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-slate-100 flex items-center gap-2">
                    <ExternalLink className="w-5 h-5 text-blue-400" />
                    Authority Portals
                  </CardTitle>
                  <CardDescription>Access authority occurrence reporting portals</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {authorities.slice(0, 5).map((auth) => (
                      <a
                        key={auth.id}
                        href={auth.portal || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between p-3 rounded bg-slate-900/50 hover:bg-slate-700/50 transition-colors group"
                      >
                        <div>
                          <p className="text-sm font-medium text-slate-200">{auth.name}</p>
                          <p className="text-xs text-slate-500">{auth.occurrence_system || 'Web Portal'}</p>
                        </div>
                        <ExternalLink className="w-4 h-4 text-slate-500 group-hover:text-blue-400 transition-colors" />
                      </a>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* API Submission Log */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-slate-100 flex items-center gap-2">
                      <Activity className="w-5 h-5 text-purple-400" />
                      API Submission Log
                    </CardTitle>
                    <CardDescription>Track direct API submission attempts and responses</CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchSubmissions}>
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {submissions.filter(s => s.submission_method === 'api').length > 0 ? (
                  <div className="space-y-2">
                    {submissions.filter(s => s.submission_method === 'api').slice(0, 10).map((sub) => (
                      <div key={sub.id} className="flex items-center justify-between p-3 rounded bg-slate-900/50">
                        <div className="flex items-center gap-3">
                          <div className={`p-1.5 rounded ${sub.status === 'acknowledged' ? 'bg-green-500/20' : sub.status === 'submitted' ? 'bg-blue-500/20' : 'bg-slate-600/30'}`}>
                            {sub.status === 'acknowledged' ? (
                              <CheckCircle2 className="w-4 h-4 text-green-400" />
                            ) : sub.status === 'submitted' ? (
                              <Send className="w-4 h-4 text-blue-400" />
                            ) : (
                              <Clock className="w-4 h-4 text-slate-400" />
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-200">{sub.submission_reference}</p>
                            <p className="text-xs text-slate-500">
                              {sub.authority?.toUpperCase()} • {sub.report_type?.replace(/_/g, ' ')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={sub.status === 'acknowledged' ? 'bg-green-500/20 text-green-400' : sub.status === 'submitted' ? 'bg-blue-500/20 text-blue-400' : 'bg-slate-500/20 text-slate-400'}>
                            {sub.status?.replace(/_/g, ' ')}
                          </Badge>
                          <span className="text-xs text-slate-500">
                            {sub.submitted_at ? new Date(sub.submitted_at).toLocaleDateString() : '-'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Plug className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400">No API submissions yet</p>
                    <p className="text-sm text-slate-500 mt-1">Direct API submissions will appear here once formal agreements are in place</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* SUBMISSION PIPELINE TAB — per-authority KPIs, SLA, DLQ replay */}
        <TabsContent value="pipeline" data-testid="pipeline-content">
          <PipelineDashboardTab token={token} />
        </TabsContent>

        <TabsContent value="reference">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-slate-100">ECCAIRS Occurrence Categories</CardTitle>
                <CardDescription>ADREP taxonomy codes for occurrence classification</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="max-h-[400px] overflow-y-auto space-y-2">
                  {occurrenceCategories.map(cat => (
                    <div key={cat.code} className="flex items-center gap-3 p-2 rounded bg-slate-900/50">
                      <span className="font-mono text-xs text-blue-400 w-16">{cat.code}</span>
                      <span className="text-sm text-slate-300">{cat.name}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-slate-100">Flight Phases</CardTitle>
                <CardDescription>Standard flight phase classifications</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="max-h-[400px] overflow-y-auto space-y-2">
                  {flightPhases.map(phase => (
                    <div key={phase.code} className="flex items-center gap-3 p-2 rounded bg-slate-900/50">
                      <span className="font-mono text-xs text-green-400 w-12">{phase.code}</span>
                      <span className="text-sm text-slate-300">{phase.name}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl bg-slate-900 border-slate-700 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-100 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-400" />
              Submission Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedSubmission && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-400">Reference</p>
                  <p className="text-slate-100 font-mono">{selectedSubmission.submission_reference}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Authority</p>
                  <p className="text-slate-100 flex items-center gap-2">
                    {getAuthorityLogo(selectedSubmission.authority)}
                    {selectedSubmission.authority?.toUpperCase()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Report Type</p>
                  <p className="text-slate-100">{selectedSubmission.report_type?.replace(/_/g, ' ')}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Status</p>
                  <Badge className={getStatusBadge(selectedSubmission.status)}>{selectedSubmission.status?.replace(/_/g, ' ')}</Badge>
                </div>
              </div>

              <Separator className="bg-slate-700" />

              {selectedSubmission.report_data && (
                <>
                  <h4 className="font-medium text-slate-200">Report Data</h4>
                  <div className="space-y-3 text-sm">
                    <div>
                      <p className="text-slate-400">Headline</p>
                      <p className="text-slate-100">{selectedSubmission.report_data.headline}</p>
                    </div>
                    {selectedSubmission.report_data.narrative && (
                      <div>
                        <p className="text-slate-400">Narrative</p>
                        <p className="text-slate-300">{selectedSubmission.report_data.narrative}</p>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-slate-400">Occurrence Date</p>
                        <p className="text-slate-100">{selectedSubmission.report_data.occurrence_date}</p>
                      </div>
                      <div>
                        <p className="text-slate-400">Category</p>
                        <p className="text-slate-100">{selectedSubmission.report_data.occurrence_category}</p>
                      </div>
                      {selectedSubmission.report_data.aircraft_registration && (
                        <div>
                          <p className="text-slate-400">Aircraft</p>
                          <p className="text-slate-100">{selectedSubmission.report_data.aircraft_registration}</p>
                        </div>
                      )}
                      {selectedSubmission.report_data.flight_number && (
                        <div>
                          <p className="text-slate-400">Flight</p>
                          <p className="text-slate-100">{selectedSubmission.report_data.flight_number}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {selectedSubmission.authority_reference && (
                <div className="p-3 rounded bg-green-500/10 border border-green-500/30">
                  <p className="text-sm text-green-300">
                    <CheckCircle2 className="w-4 h-4 inline mr-2" />
                    Authority Reference: {selectedSubmission.authority_reference}
                  </p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* API Configuration Dialog */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent className="max-w-2xl bg-slate-900 border-slate-700 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-100 flex items-center gap-2">
              <Settings className="w-5 h-5 text-blue-400" />
              Configure API - {selectedAuthority?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* API Endpoint Configuration */}
            <div className="space-y-4">
              <h4 className="font-medium text-slate-200 flex items-center gap-2">
                <Globe className="w-4 h-4" />
                API Endpoint
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label className="text-slate-300">Base URL</Label>
                  <Input
                    value={configForm.api_base_url}
                    onChange={(e) => setConfigForm({ ...configForm, api_base_url: e.target.value })}
                    placeholder="https://api.authority.gov/v1"
                    className="bg-slate-800/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">API Version</Label>
                  <Input
                    value={configForm.api_version}
                    onChange={(e) => setConfigForm({ ...configForm, api_version: e.target.value })}
                    placeholder="v1.0"
                    className="bg-slate-800/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Authentication Type</Label>
                  <Select value={configForm.auth_type} onValueChange={(v) => setConfigForm({ ...configForm, auth_type: v })}>
                    <SelectTrigger className="bg-slate-800/50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="oauth2">OAuth 2.0</SelectItem>
                      <SelectItem value="api_key">API Key</SelectItem>
                      <SelectItem value="certificate">Certificate</SelectItem>
                      <SelectItem value="basic">Basic Auth</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <Separator className="bg-slate-700" />

            {/* Credentials */}
            <div className="space-y-4">
              <h4 className="font-medium text-slate-200 flex items-center gap-2">
                <Key className="w-4 h-4" />
                Credentials
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Client ID / Username</Label>
                  <Input
                    value={configForm.client_id}
                    onChange={(e) => setConfigForm({ ...configForm, client_id: e.target.value })}
                    placeholder="client_id or username"
                    className="bg-slate-800/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Client Secret / Password</Label>
                  <Input
                    type="password"
                    value={configForm.client_secret}
                    onChange={(e) => setConfigForm({ ...configForm, client_secret: e.target.value })}
                    placeholder="••••••••••••"
                    className="bg-slate-800/50"
                  />
                </div>
                {configForm.auth_type === 'certificate' && (
                  <div className="col-span-2 space-y-2">
                    <Label className="text-slate-300">Certificate Path</Label>
                    <Input
                      value={configForm.certificate_path}
                      onChange={(e) => setConfigForm({ ...configForm, certificate_path: e.target.value })}
                      placeholder="/path/to/certificate.pem"
                      className="bg-slate-800/50"
                    />
                  </div>
                )}
              </div>
            </div>

            <Separator className="bg-slate-700" />

            {/* Organization Details */}
            <div className="space-y-4">
              <h4 className="font-medium text-slate-200 flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Organization Details
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Operator Code</Label>
                  <Input
                    value={configForm.operator_code}
                    onChange={(e) => setConfigForm({ ...configForm, operator_code: e.target.value })}
                    placeholder="AGD"
                    className="bg-slate-800/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">AOC Number</Label>
                  <Input
                    value={configForm.aoc_number}
                    onChange={(e) => setConfigForm({ ...configForm, aoc_number: e.target.value })}
                    placeholder="AOC-12345"
                    className="bg-slate-800/50"
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label className="text-slate-300">Organization Name</Label>
                  <Input
                    value={configForm.organization_name}
                    onChange={(e) => setConfigForm({ ...configForm, organization_name: e.target.value })}
                    placeholder="AeroGuard Airlines"
                    className="bg-slate-800/50"
                  />
                </div>
              </div>
            </div>

            <Separator className="bg-slate-700" />

            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-slate-300">Notes</Label>
              <Textarea
                value={configForm.notes}
                onChange={(e) => setConfigForm({ ...configForm, notes: e.target.value })}
                placeholder="Additional notes about this configuration..."
                className="bg-slate-800/50"
                rows={3}
              />
            </div>

            {/* Status */}
            <div className="space-y-2">
              <Label className="text-slate-300">Configuration Status</Label>
              <Select value={configForm.status} onValueChange={(v) => setConfigForm({ ...configForm, status: v })}>
                <SelectTrigger className="bg-slate-800/50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="configured">Configured (Ready to Test)</SelectItem>
                  <SelectItem value="testing">Testing</SelectItem>
                  <SelectItem value="pending_approval">Pending Approval</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigDialog(false)}>Cancel</Button>
            <Button onClick={saveApiConfig} disabled={savingConfig}>
              {savingConfig ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Configuration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Deadline Dialog */}
      <Dialog open={showDeadlineDialog} onOpenChange={setShowDeadlineDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-orange-400" />
              Add Regulatory Deadline
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Authority</Label>
              <Select 
                value={deadlineForm.authority} 
                onValueChange={(v) => setDeadlineForm({...deadlineForm, authority: v})}
              >
                <SelectTrigger className="bg-slate-800">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gaca">🇸🇦 GACA (Saudi Arabia)</SelectItem>
                  <SelectItem value="easa">🇪🇺 EASA (European Union)</SelectItem>
                  <SelectItem value="faa">🇺🇸 FAA (United States)</SelectItem>
                  <SelectItem value="icao">🌍 ICAO (International)</SelectItem>
                  <SelectItem value="caa_uk">🇬🇧 CAA UK (United Kingdom)</SelectItem>
                  <SelectItem value="casa">🇦🇺 CASA (Australia)</SelectItem>
                  <SelectItem value="tcca">🇨🇦 TCCA (Canada)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Requirement Name</Label>
              <Input
                placeholder="e.g., Annual Safety Report"
                value={deadlineForm.requirement}
                onChange={(e) => setDeadlineForm({...deadlineForm, requirement: e.target.value})}
                className="bg-slate-800"
              />
            </div>
            <div className="space-y-2">
              <Label>Due Date</Label>
              <Input
                type="date"
                value={deadlineForm.due_date}
                onChange={(e) => setDeadlineForm({...deadlineForm, due_date: e.target.value})}
                className="bg-slate-800"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                placeholder="Additional notes about this deadline..."
                value={deadlineForm.notes}
                onChange={(e) => setDeadlineForm({...deadlineForm, notes: e.target.value})}
                className="bg-slate-800"
                rows={2}
              />
            </div>
            <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <p className="text-sm text-blue-300 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Email notifications will be sent at 30, 14, and 7 days before the deadline
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeadlineDialog(false)}>Cancel</Button>
            <Button 
              onClick={createDeadline} 
              disabled={!deadlineForm.requirement || !deadlineForm.due_date}
              className="bg-orange-500 hover:bg-orange-600"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Deadline
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
